import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  Link,
} from '@mui/material';
import { Menu as MenuIcon, Phone, Email } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import krawanetterLogo from '../assets/logos/kranawetter-logo.png';
import { trackConversion } from '../utils/analytics';
const krawanetterLogoWebp = '/assets/logos/optimized/kranawetter-logo.webp';
const DRIVER_PORTAL_URL = 'https://portal.kranawettertransport.com';

const Navbar = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const SCROLL_OFFSET_PX = 90;
  const scrollToSection = (sectionId) => {
    handleMenuClose();
    
    // If we're not on the home page, navigate there first
    if (location.pathname !== '/') {
      // Navigate to home and scroll to section after a brief delay
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          const y = element.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET_PX;
          window.scrollTo({ top: y, behavior: 'smooth' });
        }
      }, 100);
    } else {
      // We're already on home page, just scroll
      const element = document.getElementById(sectionId);
      if (element) {
        const y = element.getBoundingClientRect().top + window.pageYOffset - SCROLL_OFFSET_PX;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  };

  const navItems = [
    { label: 'Services', action: () => scrollToSection('services') },
    { label: 'Fleet', action: () => navigate('/fleet') },
    { label: 'Drivers', action: () => scrollToSection('drivers') },
    { label: 'Contact', action: () => scrollToSection('contact') },
  ];

  return (
    <AppBar 
      position="sticky" 
      sx={{ 
        backgroundColor: 'white',
        boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        borderBottom: '1px solid #e0e0e0'
      }}
    >
      <Toolbar sx={{ py: 1 }}>
        {/* Logo */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'flex-start',
            flexGrow: 1,
            cursor: 'pointer'
          }}
          onClick={() => navigate('/')}
        >
          <picture>
            <source srcSet={krawanetterLogoWebp} type="image/webp" />
            <Box
              component="img"
              src={krawanetterLogo}
              alt="Transport Logo"
              width={114}
              height={81}
              sx={{
                height: 65,
                width: 'auto',
                maxWidth: 250,
                objectFit: 'contain',
                ml: 2
              }}
            />
          </picture>
        </Box>

        {/* Contact Info */}
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, alignItems: 'center', mr: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 3 }}>
            <Phone sx={{ color: 'primary.main', fontSize: 20, mr: 1 }} />
            <Link
              href="tel:+15738662866"
              underline="hover"
              color="text.primary"
              variant="body2"
              sx={{ fontWeight: 500 }}
              onClick={() => trackConversion('phone_click')}
            >
              (573) 866-2866
            </Link>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Email sx={{ color: 'primary.main', fontSize: 20, mr: 1 }} />
            <Link
              href="mailto:dispatch@kranawettertransport.com"
              underline="hover"
              color="text.primary"
              variant="body2"
              sx={{ fontWeight: 500 }}
            >
              dispatch@kranawettertransport.com
            </Link>
          </Box>
        </Box>

        {/* Desktop Navigation */}
        {!isMobile ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {navItems.map((item) => (
              <Button
                key={item.label}
                onClick={item.action}
                sx={{
                  color: 'text.primary',
                  fontWeight: 500,
                  '&:hover': {
                    backgroundColor: 'primary.light',
                    color: 'white',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
            <Button
              component="a"
              href={DRIVER_PORTAL_URL}
              sx={{
                color: 'text.primary',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'white',
                },
              }}
            >
              Driver login
            </Button>
            <Button
              variant="contained"
              color="secondary"
              component="a"
              href="/apply"
              sx={{ ml: 2 }}
            >
              Drive for Us
            </Button>
          </Box>
        ) : (
          // Mobile Menu
          <>
            <Button
              variant="contained"
              color="primary"
              size="small"
              href="tel:+15738662866"
              startIcon={<Phone />}
              onClick={() => trackConversion('phone_click')}
              sx={{
                mr: 1,
                display: { xs: 'inline-flex', md: 'none' }
              }}
            >
              Call
            </Button>
            <IconButton
              edge="end"
              color="inherit"
              aria-label="menu"
              onClick={handleMenuOpen}
              sx={{ color: 'text.primary' }}
            >
              <MenuIcon />
            </IconButton>
              <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              {navItems.map((item) => (
                <MenuItem
                  key={item.label}
                  onClick={() => {
                    item.action();
                  }}
                >
                  {item.label}
                </MenuItem>
              ))}
              <MenuItem
                component="a"
                href={DRIVER_PORTAL_URL}
                onClick={handleMenuClose}
              >
                Driver login
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  navigate('/apply');
                }}
              >
                <Typography color="secondary" fontWeight="bold">
                  Drive for Us
                </Typography>
              </MenuItem>
            </Menu>
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar; 